import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/beautician-real.jpg";

export const beauticians: Persona = {
  slug: "beauticians-beauty-salons",
  profession: { hu: "kozmetikus", en: "beautician" },
  professionPlural: { hu: "kozmetikusok", en: "beauticians" },
  professionGenitive: { hu: "kozmetikusként", en: "as a beautician" },
  heroH1: {
    hu: "Tele naptár, tele szalon. Egy chat üzenetből.",
    en: "Full calendar, full salon. From one chat message.",
  },
  heroSub: {
    hu: "AI weboldal, online foglalás, csomagok és bérletek: egy platformon, szóló kozmetikusoknak és szalonoknak.",
    en: "AI website, online booking, packages and passes: one platform for solo beauticians and salons.",
  },
  heroImage,
  heroImageAlt: "Beautician performing a facial treatment in the salon",
  chatExamplePrompt: {
    hu: "Tedd be Mari kezelést szombat 11-re, anti-aging csomaggal",
    en: "Book Mari for Saturday 11 AM with the anti-aging package",
  },
  chatExampleResponse: {
    hu: "Bejegyeztem szombat 11-re, 90 perc. Anti-aging csomagot hozzáadtam, az ár 24 500 Ft. Marinak visszaigazolást küldtem.",
    en: "Booked Saturday 11 AM, 90 minutes. Added the anti-aging package, total 24,500 HUF. Confirmation sent to Mari.",
  },
  accentColor: "pink",

  defaultMode: "team",
  teamSize: { min: 1, max: 10 },
  soloLabel: { hu: "Szóló kozmetikus", en: "Solo beautician" },
  teamLabel: {
    hu: "Szalon több kozmetikussal",
    en: "Salon with multiple beauticians",
  },

  soloDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Megnyitod a napot: 6 időpont, 2 új ügyfél. Az AI már visszaigazolt mindenkit.",
        en: "Open the day: 6 appointments, 2 new clients. AI has already confirmed everyone.",
      },
    },
    {
      time: "10:30",
      what: {
        hu: "Új lead a Google-ról. Az AI a stílusodban válaszolt, már le is foglalta a próba arctisztítást.",
        en: "New lead from Google. AI replied in your tone, already booked a trial facial.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Egy ügyfél lemond. Az AI felajánlja az időpontot a várólista első emberének, 60 mp múlva tele a slot.",
        en: "A client cancels. AI offers the slot to the first person on the waitlist: full again in 60 seconds.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Új akció chatben: '15% kedvezmény hydrafacial-re csütörtökön'. Az AI Instagramon és e-mailben kiküldi.",
        en: "New promo in chat: '15% off hydrafacial on Thursday'. AI sends it to Instagram and email.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Napzárás: 12 kezelés, 78 000 Ft, 3 új tag. Számlák automatikusan kiállítva.",
        en: "End of day: 12 treatments, 78,000 HUF, 3 new members. Invoices issued automatically.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Szalon dashboard: 3 kozmetikusod 22 időpontot ad ma. Mindenki saját nézettel, te mindent látsz.",
        en: "Salon dashboard: your 3 beauticians have 22 appointments today. Each has their own view, you see everything.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "Réka beteg. Az AI áthelyezi 6 ügyfelét Annához és Évához, mindenkit értesít.",
        en: "Réka is sick. AI moves her 6 clients to Anna and Éva, notifies everyone.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Új kozmetikus, Lilla érkezik. Felveszed, 5 percen belül publikálódik a szalon oldalán.",
        en: "New beautician Lilla joins. You add her, her profile goes live on the salon site within 5 minutes.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Az AI mutatja: Anna szakterülete a hydrafacial: a héten +32% bevétel. Megmutatod a többieknek.",
        en: "AI shows: Anna's specialty is hydrafacial: +32% revenue this week. You share it with the team.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Hétvégi 'brunch & beauty' akció telik. AI várólistára teszi a túljelentkezőket.",
        en: "Weekend 'brunch & beauty' promo fills up. AI puts overflow on the waitlist.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Bevétel kozmetikusonként, jutalék-számítás automatikusan, számlák kiállítva.",
        en: "Revenue per beautician, automatic commission calculation, invoices issued.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: {
        hu: "Naptár, ügyfél, csomag: egy helyen",
        en: "Calendar, clients, packages: in one place",
      },
      body: {
        hu: "Nem kell Excel, nem kell papír. Mindent egy helyen kezelsz.",
        en: "No Excel, no paper. Manage everything in one account.",
      },
    },
    {
      icon: "Bot",
      title: { hu: "AI asszisztens neked", en: "AI assistant for you" },
      body: {
        hu: "Egy chat üzenetből foglalsz, számlázol, kampányt indítasz: az AI a te oldaladon dolgozik a háttérben.",
        en: "Book, invoice, launch a campaign: all from a single chat message. AI works in the background for you.",
      },
    },
    {
      icon: "CreditCard",
      title: { hu: "Csomagok és bérletek", en: "Packages and passes" },
      body: {
        hu: "5 alkalmas hydrafacial, anti-aging csomag: automatikus levonás foglaláskor.",
        en: "5-session hydrafacial, anti-aging package: auto-deducted at booking.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan kozmetikus, egy ár",
        en: "Unlimited beauticians, one price",
      },
      body: {
        hu: "1 vagy 10 fő: ugyanaz a havidíj. Mindenki saját naptárral és ügyfélkörrel.",
        en: "1 or 10: same monthly fee. Each with their own calendar and client base.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Tulaj, recepciós, kozmetikus: mindenki csak azt látja, amihez joga van.",
        en: "Owner, receptionist, beautician: everyone sees only what they're allowed to.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja az ügyfeleket", en: "AI assigns clients" },
      body: {
        hu: "Új lead: az AI szakterület és szabad slot alapján a megfelelő kozmetikushoz osztja.",
        en: "New lead: AI assigns them to the right beautician based on specialty and free slots.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Automatikus jutalék-elszámolás",
        en: "Automatic commission settlement",
      },
      body: {
        hu: "Ki mennyit dolgozott, milyen bevételt hozott: exportálható, Számlázz.hu kompatibilis.",
        en: "Who worked how much, what revenue they brought in: exportable, Számlázz.hu compatible.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: {
        hu: "Kezelési csomagok és bérletek",
        en: "Treatment packages and passes",
      },
      body: {
        hu: "4 és 8 alkalmas csomagok (pl. hydrafacial, anti-aging sorozat), automata bérletvonás, lejárati emlékeztető.",
        en: "4/8-session packages (e.g. hydrafacial, anti-aging series), auto-deduction, expiry reminders.",
      },
    },
    {
      icon: "Bell",
      title: { hu: "No-show védelem", en: "No-show protection" },
      body: {
        hu: "Foglaláskor előleg, 24 órás emlékeztető SMS/email/WhatsApp-en, lemondási szabályok: drasztikusan csökken a hiányzás.",
        en: "Deposit at booking, 24-hour SMS/email/WhatsApp reminders, cancellation rules: no-shows plummet.",
      },
    },
    {
      icon: "CreditCard",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payment",
      },
      body: {
        hu: "Bankkártya, Apple/Google Pay foglaláskor, helyszíni készpénz is: minden tranzakció egy felületen, automata számlával.",
        en: "Card, Apple/Google Pay at booking, in-person cash too: every transaction in one place, auto-invoiced.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Marketing automatika", en: "Marketing automation" },
      body: {
        hu: "Inaktív ügyfeleknek emlékeztető email, Instagram poszt új kezelésről: egy klikkre, a Számlázz.hu-val és Instagrammal együtt működik.",
        en: "Win-back emails for inactive clients, Instagram posts for new treatments: one click, works with Számlázz.hu and Instagram.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "Több munkatársunk dolgozik a szalonban: hogy kezeli a csapatot?",
        en: "We have multiple staff in the salon: how does it handle the team?",
      },
      answer: {
        hu: "Minden kozmetikus saját Bespire-fiókkal dolgozik a szalonon belül. Saját naptár, saját ügyfélkör, saját jutalék-elszámolás. Te tulajdonosként mindent látsz, ők csak a sajátjukat. Korlátlan kozmetikust adhatsz hozzá, az ár nem változik.",
        en: "Each beautician has their own Bespire account inside your salon. Own calendar, own client base, own commission. As owner you see everything, they only see theirs. Add unlimited beauticians: the price doesn't change.",
      },
    },
    {
      question: {
        hu: "Az ügyfeleim szeretnek telefonon foglalni",
        en: "My clients like to book by phone",
      },
      answer: {
        hu: "Maradhat a telefon: a recepciós a Bespire-ben jegyzi be 10 mp alatt. De a 24/7 online foglalás odavonzza a fiatalabb ügyfélkört is, akiknek 22:00-kor jut eszükbe lefoglalni a hétvégi kezelést.",
        en: "Phone bookings stay: the receptionist enters them in Bespire in 10 seconds. But 24/7 online booking also attracts younger clients who remember at 10 PM to book a weekend treatment.",
      },
    },
    {
      question: {
        hu: "Csomagokat és bérleteket hogyan kezelek?",
        en: "How do I manage packages and passes?",
      },
      answer: {
        hu: "Bármilyen csomag: 5 alkalmas hydrafacial, 3 hónapos anti-aging sorozat, ajándékutalvány. Foglaláskor automatikusan levonódik. Lejárat előtt az AI emlékeztetőt küld.",
        en: "Any kind of package: 5-session hydrafacial, 3-month anti-aging series, gift cards. Auto-deducted at booking. AI reminds clients before expiry.",
      },
    },
    {
      question: {
        hu: "El tudom hozni a régi ügyfél-kartonjaimat?",
        en: "Can I bring over my old client cards?",
      },
      answer: {
        hu: "Igen. CSV/Excel importtal vagy a Beüzemelés szolgáltatásban átvesszük a teljes ügyfélbázist, csomagállományt, korábbi kezelési előzményt.",
        en: "Yes. Via CSV/Excel import, or our Setup service migrates the full client base, package balances and treatment history.",
      },
    },
  ],

  monetization: [
    {
      icon: "Heart",
      title: { hu: "Arckezelések", en: "Facials" },
      examples: [
        { hu: "Klasszikus arctisztítás", en: "Classic facial cleansing" },
        { hu: "Hydrafacial", en: "Hydrafacial" },
        { hu: "Mezoterápia", en: "Mesotherapy" },
      ],
      priceHint: {
        hu: "Példa: 60 kezelés × 16 000 Ft = 960 000 Ft / hó (arctisztítás 12:18e, hydrafacial 25:35e, mezoterápia 18:25e)",
        en: "Example: 60 treatments × 16,000 HUF = 960,000 HUF / month (facials 12:18k, hydrafacial 25:35k, meso 18:25k)",
      },
    },
    {
      icon: "Sparkles",
      title: {
        hu: "Anti-aging és kozmetikai csomagok",
        en: "Anti-aging and beauty packages",
      },
      examples: [
        { hu: "3 hónapos anti-aging", en: "3-month anti-aging" },
        { hu: "Bőrújító sorozat", en: "Skin renewal series" },
        { hu: "Esküvői előkészítő", en: "Bridal prep" },
      ],
      priceHint: {
        hu: "Példa: 8 csomag × 65 000 Ft = 520 000 Ft / hó",
        en: "Example: 8 packages × 65,000 HUF = 520,000 HUF / month",
      },
    },
    {
      icon: "Smile",
      title: { hu: "Smink és szépségápolás", en: "Makeup and beauty care" },
      examples: [
        { hu: "Alkalmi smink", en: "Occasion makeup" },
        { hu: "Szempilla-festés", en: "Lash tinting" },
        { hu: "Szemöldök formázás", en: "Brow shaping" },
      ],
      priceHint: {
        hu: "Példa: 40 alkalom × 8 500 Ft = 340 000 Ft / hó",
        en: "Example: 40 sessions × 8,500 HUF = 340,000 HUF / month",
      },
    },
    {
      icon: "Package",
      title: { hu: "Termékek és kiegészítők", en: "Products and add-ons" },
      examples: [
        { hu: "Otthoni ápolási kit", en: "Home-care kit" },
        { hu: "Premium krémek", en: "Premium creams" },
        { hu: "Ajándékkártya", en: "Gift card" },
      ],
      priceHint: {
        hu: "Passzív bevétel: ~120 000 Ft / hó (átlag)",
        en: "Passive revenue: ~120,000 HUF / month (average)",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Bérletek és tagságok", en: "Passes and memberships" },
      examples: [
        { hu: "5 alkalmas hydrafacial", en: "5-session hydrafacial" },
        { hu: "Havi VIP", en: "Monthly VIP" },
        { hu: "Éves ápolási kártya", en: "Annual care card" },
      ],
      priceHint: {
        hu: "Szalon-szint: 4 kozmetikus × 35 bérleti ügyfél × 22 000 Ft = 3 080 000 Ft / hó potenciál",
        en: "Salon level: 4 beauticians × 35 pass holders × 22,000 HUF = 3,080,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+45%",
      label: {
        hu: "kihasználtság / kozmetikus",
        en: "utilization / beautician",
      },
    },
    {
      value: "-72%",
      label: { hu: "no-show előleg-rendszerrel", en: "no-shows with deposits" },
    },
    {
      value: "4.8★",
      label: { hu: "ügyfél-vélemények átlaga", en: "client review average" },
    },
    { value: "30 perc", label: { hu: "teljes beüzemelés", en: "full setup" } },
  ],
  testimonial: {
    quote: {
      hu: "Régen 3 különböző rendszerrel dolgoztunk: naptár, bérletek, számlázás. A Bespire-rel ez most egy hely, és a recepciós felszabadult az ügyfélkiszolgálásra.",
      en: "We used to juggle 3 different systems: calendar, passes, invoicing. With Bespire it's now one place, and the receptionist is free to focus on clients.",
    },
    name: "Tóth Anikó",
    role: { hu: "Szépségszalon-vezető", en: "Beauty salon manager" },
    city: { hu: "Szeged", en: "Szeged" },
    avatarInitials: "TA",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a kezeléseidet",
        en: "Tell us your treatments in chat",
      },
      body: {
        hu: "Arctisztítás, hydrafacial, mezoterápia, anti-aging csomag: az AI katalógust épít.",
        en: "Facials, hydrafacial, meso, anti-aging packages: AI builds your catalogue.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a szalon oldalt és csomagokat",
        en: "AI builds the salon site and packages",
      },
      body: {
        hu: "Saját brand, csomag-árazás, foglalási szabályok.",
        en: "Your branding, package pricing, booking rules.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Importáljuk az ügyfél-kartonokat",
        en: "We import client cards",
      },
      body: {
        hu: "Allergiák, korábbi kezelések, fotók előtte-utána: egy profilban.",
        en: "Allergies, past treatments, before/after photos: in one profile.",
      },
    },
    {
      step: 4,
      title: {
        hu: "Élő, online foglalás 24/7",
        en: "Live, online booking 24/7",
      },
      body: {
        hu: "Az ügyfél mobilról foglal, te a szalonban dolgozol.",
        en: "The client books on mobile while you focus on the salon.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Stripe",
    "SimplePay",
    "Apple Pay",
    "Google Pay",
    "Meta",
    "WhatsApp Business",
    "Mailchimp",
  ],
  breadcrumbLabel: {
    hu: "Kozmetikusok és szépségszalonok",
    en: "Beauticians & beauty salons",
  },
  similarPersonas: [
    "hairdressers-barbers",
    "nail-technicians",
    "massage-therapists",
  ],
};
